import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";

import {
  defaultRow, hasChanges, modelRows, move, problem, ratesText, savePayload, savedMessage,
  setLabel, setOffered, visibleRows, visionText, type AdminModelsAnswer, type ModelRow,
} from "../admin-models.js";
import {
  canRestart, countsText, lastRunningText, restartAllMessage, restartAllQuestion, restartBlocker, restartQuestion, stateOf, userText,
  type AdminWorkspace,
} from "../admin-workspaces.js";
import { hostedApi, type HostedSession } from "../hosted-api.js";
import { Admin } from "./Admin.js";
import { BrandMark } from "./BrandMark.js";
import { ConfirmProvider, useConfirm, useConfirmHost } from "./ConfirmDialog.js";
import { Login } from "./Login.js";
import { ActiveGlide } from "./ActiveGlide.js";

const message = (cause: unknown, fallback: string): string => (cause instanceof Error ? cause.message : fallback);

/**
 * /admin. The cloud app (its session says `demo: false`) has its own
 * administration, for what the app decides: which models its bots may run,
 * and when its workspaces restart to pick up its settings. Who is an operator
 * is the app Worker's own list (ADMIN_USER_IDS), carried on the session as
 * `admin`, and checked again on every admin request. Any other host is the
 * local hosted demo, whose administration has its own password.
 */
export function AdminSite(): React.JSX.Element {
  const [session, setSession] = useState<HostedSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    hostedApi.session()
      .then((next) => { if (!cancelled) { setSession(next); setError(null); } })
      .catch((cause) => { if (!cancelled) setError(message(cause, "Cannot reach the service.")); });
    return () => { cancelled = true; };
  }, [attempt]);

  if (error != null && session == null) {
    return <main className="admin-page"><p className="notice" role="alert">{error}</p><button type="button" onClick={() => setAttempt(attempt + 1)}>Try again</button></main>;
  }
  if (session == null) return <main className="admin-page"><p className="muted">Loading…</p></main>;
  if (session.demo !== false) return <Admin />;
  if (!session.authenticated) return <Login demo={false} signupUrl={session.signupUrl} onAuthenticated={setSession} />;
  const signOut = (): void => {
    void hostedApi.logout().catch(() => { /* signed out here either way */ })
      .then(() => setSession({ enabled: true, authenticated: false, demo: false, ...(session.signupUrl == null ? {} : { signupUrl: session.signupUrl }) }));
  };
  if (session.admin !== true) {
    return <main className="admin-page">
      <header className="admin-header"><div><div className="brand">ark-bot · administration</div><h1>Not an administrator</h1>
        <p className="muted">{session.account?.username ?? "This account"} can use the app, but not its administration.</p></div></header>
      <div className="hosted-actions"><a className="linkish" href="/">Back to the app</a><button type="button" onClick={signOut}>Sign in as someone else</button></div>
    </main>;
  }
  return <AdminConsole account={session.account?.username ?? ""} onSignOut={signOut} />;
}

/** A line icon, drawn in the text's colour. */
function Icon({ d, size = 18 }: { readonly d: string; readonly size?: number }): React.JSX.Element {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d={d} /></svg>;
}

type PageId = "models" | "workspaces";

/** The console's pages, each at its own address after the # (#/models, #/workspaces); the first is where /admin opens. */
const PAGES: readonly { readonly id: PageId; readonly label: string; readonly icon: string }[] = [
  { id: "models", label: "Models", icon: "M12 3.5l2 5.5 5.5 2-5.5 2-2 5.5-2-5.5-5.5-2 5.5-2z" },
  { id: "workspaces", label: "Workspaces", icon: "M4 4.5h16v6.5H4zM4 13h16v6.5H4zM8 7.75h.01M8 16.25h.01" },
];

const pageOf = (hash: string): PageId => PAGES.find((page) => hash === `#/${page.id}`)?.id ?? "models";

function usePage(): PageId {
  const [page, setPage] = useState(() => pageOf(location.hash));
  useEffect(() => {
    const changed = (): void => setPage(pageOf(location.hash));
    window.addEventListener("hashchange", changed);
    return () => window.removeEventListener("hashchange", changed);
  }, []);
  return page;
}

/**
 * The administration, shaped like the platform's own: a sidebar with a link
 * per page, the way back to the app and who is signed in; beside it the page.
 * A page once opened stays mounted while another shows, so an unsaved offer
 * survives a look at the workspaces.
 */
export function AdminConsole({ account, onSignOut }: { readonly account: string; readonly onSignOut: () => void }): React.JSX.Element {
  const page = usePage();
  const [opened, setOpened] = useState<ReadonlySet<PageId>>(() => new Set([page]));
  if (!opened.has(page)) setOpened(new Set([...opened, page]));
  const { confirm, dialog } = useConfirmHost();
  return <ConfirmProvider value={confirm}>
    <div className="admin-console">
      <aside className="admin-side">
        <a className="admin-brand" href="#/models"><BrandMark /><span>ark-bot · administration</span></a>
        <nav className="admin-navs" aria-label="Administration">
          <ActiveGlide selector='[aria-current="page"]' className="glide-row glide-tab" />
          {PAGES.map((one) => <a key={one.id} className="admin-nav" href={`#/${one.id}`} aria-current={one.id === page ? "page" : undefined}><Icon d={one.icon} />{one.label}</a>)}
        </nav>
        <div className="admin-spacer" />
        <a className="admin-nav admin-open" href="/"><Icon d="M19 12H5M11 6l-6 6 6 6" />Open the app</a>
        <div className="admin-me">
          <span className="admin-me-text"><b title={account}>{account}</b><span>Administrator</span></span>
          <button type="button" className="icon" aria-label="Sign out" title="Sign out" onClick={onSignOut}><Icon d="M15 4h4v16h-4M10 8l-4 4 4 4M6 12h10" size={17} /></button>
        </div>
      </aside>
      <main className="admin-content">
        {opened.has("models") ? <div hidden={page !== "models"}><ModelsPage /></div> : null}
        {opened.has("workspaces") ? <div hidden={page !== "workspaces"}><WorkspacesPage /></div> : null}
      </main>
    </div>
    {dialog}
  </ConfirmProvider>;
}

/** A page's head: what the page is, on the left; its actions and Refresh on the right. */
function PageHead({ title, description, busy, onRefresh, refreshHeld = null, children }: {
  readonly title: string;
  readonly description: string;
  readonly busy: boolean;
  readonly onRefresh: () => void;
  /** Why Refresh must wait, or null when it may go. */
  readonly refreshHeld?: string | null;
  readonly children?: ReactNode;
}): React.JSX.Element {
  return <header className="admin-page-head">
    <div><h1>{title}</h1><p className="muted">{description}</p></div>
    <div className="admin-page-actions">
      {children}
      <button type="button" disabled={busy || refreshHeld != null} title={refreshHeld ?? undefined} onClick={onRefresh}><Icon d="M20 12a8 8 0 1 1-2.3-5.7M20 4v5h-5" size={15} />Refresh</button>
    </div>
  </header>;
}

/** The Models page: the models users can pick for each bot, from the provider's price list. */
function ModelsPage(): React.JSX.Element {
  const [answer, setAnswer] = useState<AdminModelsAnswer | null>(null);
  const [rows, setRows] = useState<ModelRow[]>([]);
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    const next = await hostedApi.adminModels();
    setAnswer(next);
    setRows(modelRows(next));
  }, []);
  useEffect(() => { void load().catch((cause) => setError(message(cause, "Could not load the models."))); }, [load]);

  const edit = (next: ModelRow[]): void => {
    setRows(next);
    setNotice(null);
  };
  const refresh = (): void => {
    setBusy(true); setError(null); setNotice(null);
    void load().catch((cause) => setError(message(cause, "Could not load the models."))).finally(() => setBusy(false));
  };
  const save = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    const blocker = problem(rows);
    if (blocker != null) { setError(blocker); return; }
    setBusy(true); setError(null); setNotice(null);
    try {
      await hostedApi.saveAdminModels(savePayload(rows).models);
      const said = savedMessage(rows);
      await load();
      setNotice(said);
    } catch (cause) { setError(message(cause, "Could not save the offer.")); } finally { setBusy(false); }
  };

  const offered = rows.filter((row) => row.offered);
  const changed = answer != null && hasChanges(answer, rows);
  const shown = visibleRows(rows, query);
  const first = defaultRow(rows);

  return <section className="admin-section">
    <PageHead title="Bot models" description="The models users can choose for each bot, from the provider’s price list." busy={busy} onRefresh={refresh} refreshHeld={changed ? "Save or discard the changes first." : null} />
    {error != null ? <p className="notice" role="alert">{error}</p> : null}
    {notice != null ? <p className="admin-success" role="status">{notice}</p> : null}
    <section className="admin-legend">
      <p><b>Offered</b>Tick a model to let users pick it in a bot’s settings, under the name you give it. They see the offer in this order.</p>
      <p><b>Default</b>The first model on the offer: what a bot runs until its user picks one. Move another to the top to make it the default. With nothing offered, bots have no model to run.</p>
      <p><b>No longer priced</b>A model the provider stops pricing leaves the offer by itself, and bots set to it run the default. Saving drops it for good.</p>
    </section>
    {answer == null ? (error == null ? <p className="muted">Loading the price list…</p> : null) : <form className="admin-card" onSubmit={(event) => void save(event)}>
      <div className="admin-card-heading"><h2>Offer</h2><span className="admin-chip">{offered.length === 0 ? "Nothing offered" : `${offered.length} offered`}</span></div>
      <p className="muted">A change reaches bots within ten minutes, with no restart. A request is charged at the price of the model that runs it.</p>
      <div className="admin-fields admin-models-fields">
        <label>Find a model<input type="search" maxLength={150} autoComplete="off" placeholder="claude, deepseek, glm…" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
      </div>
      <div className="admin-table-wrap"><table className="admin-table admin-models">
        <thead><tr><th className="pick">Offer</th><th>Model</th><th>Name users see</th><th className="num">Credits per 1M tokens</th><th title="Whether the model takes images. A bot on a model without Vision leaves images out.">Vision</th><th className="order">Order</th></tr></thead>
        <tbody>
          {shown.map((row) => {
            const place = offered.findIndex((candidate) => candidate.model === row.model);
            return <tr key={row.model} className={row.offered ? "is-picked" : row.withdrawn ? "is-withdrawn" : undefined}>
              <td className="pick"><input type="checkbox" checked={row.offered} disabled={row.withdrawn || busy} aria-label={`Offer ${row.model}`} onChange={(event) => edit(setOffered(rows, row.model, event.target.checked))} /></td>
              <td><code>{row.model}</code>{row === first ? <span className="admin-chip">Default</span> : null}{row.withdrawn ? <span className="admin-chip is-warn">No longer priced</span> : null}</td>
              <td><input type="text" maxLength={60} value={row.label} placeholder={row.model} disabled={!row.offered || busy} aria-label={`Name users see for ${row.model}`} onChange={(event) => edit(setLabel(rows, row.model, event.target.value))} /></td>
              <td className="num">{ratesText(row)}</td>
              <td>{visionText(row)}</td>
              <td className="order">{row.offered ? <div className="admin-order">
                <button type="button" disabled={busy || place === 0} aria-label={`Move ${row.model} up`} onClick={() => edit(move(rows, row.model, -1))}>↑</button>
                <button type="button" disabled={busy || place === offered.length - 1} aria-label={`Move ${row.model} down`} onClick={() => edit(move(rows, row.model, 1))}>↓</button>
              </div> : null}</td>
            </tr>;
          })}
        </tbody>
      </table>
      {shown.length === 0 ? <p className="muted">{query.trim() === "" ? "No priced models yet. The hourly price sync fills this list." : "No priced model matches that search."}</p> : null}</div>
      <div className="admin-save-row">
        <button className="primary" disabled={busy || !changed}>{busy ? "Saving…" : "Save offer"}</button>
        {changed ? <><span className="muted">Unsaved changes</span><button type="button" disabled={busy} onClick={refresh}>Discard changes</button></> : null}
      </div>
    </form>}
  </section>;
}

/**
 * The Workspaces page: every Ark Bot workspace with its user, and a restart
 * for a running one, or for every running one at once, so it starts again
 * with the app's current settings. Both ask first.
 */
function WorkspacesPage(): React.JSX.Element {
  const confirm = useConfirm();
  const [workspaces, setWorkspaces] = useState<readonly AdminWorkspace[] | null>(null);
  /** What is under way: a workspace's ID while it is asked to restart, "all", or "refresh". */
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setWorkspaces((await hostedApi.adminWorkspaces()).workspaces);
  }, []);
  useEffect(() => { void load().catch((cause) => setError(message(cause, "Could not load the workspaces."))); }, [load]);

  const refresh = (): void => {
    setPending("refresh"); setError(null); setNotice(null);
    void load().catch((cause) => setError(message(cause, "Could not load the workspaces."))).finally(() => setPending(null));
  };
  /** Runs a restart and says what it did. The list reloads either way: a refusal usually means it was out of date. */
  const act = async (key: string, run: () => Promise<string>): Promise<void> => {
    setPending(key); setError(null); setNotice(null);
    try {
      const said = await run();
      await load().catch(() => { /* it went; Refresh catches the list up */ });
      setNotice(said);
    } catch (cause) {
      setError(message(cause, "Could not restart."));
      await load().catch(() => { /* the error above is the one to read */ });
    } finally { setPending(null); }
  };

  const list = workspaces ?? [];
  const running = list.filter(canRestart).length;
  const restart = async (workspace: AdminWorkspace): Promise<void> => {
    if (!(await confirm(restartQuestion(workspace)))) return;
    await act(workspace.id, async () => `Restarting ${userText((await hostedApi.restartWorkspace(workspace.id)).workspace)}.`);
  };
  const restartAll = async (): Promise<void> => {
    if (!(await confirm(restartAllQuestion(running)))) return;
    await act("all", async () => restartAllMessage(await hostedApi.restartAllWorkspaces()));
  };
  const now = Date.now();

  return <section className="admin-section">
    <PageHead title="Workspaces" description="Every Ark Bot workspace, with its user." busy={pending != null} onRefresh={refresh}>
      <button type="button" disabled={pending != null || running === 0} title={running === 0 ? "No running workspace to restart." : undefined} onClick={() => void restartAll()}>{pending === "all" ? "Restarting…" : "Restart all"}</button>
    </PageHead>
    {error != null ? <p className="notice" role="alert">{error}</p> : null}
    {notice != null ? <p className="admin-success" role="status">{notice}</p> : null}
    <section className="admin-legend">
      <p><b>Restart</b>Backs the workspace up and starts it again, with the app’s current settings. Whatever its bots are doing is cut off; files and chats are kept.</p>
      <p><b>Restart all</b>Restarts every running workspace the same way. Stopped workspaces are left alone: they get the current settings whenever they next start.</p>
      <p><b>Current settings</b>Every start, of any kind, gets the app’s current settings: a restart here, one the user asks for, or a stopped workspace starting again.</p>
    </section>
    {workspaces == null ? (error == null ? <p className="muted">Loading the workspaces…</p> : null) : <>
      <p className="admin-counts">{countsText(workspaces)}</p>
      <div className="admin-card admin-table-card"><div className="admin-table-wrap"><table className="admin-table admin-workspaces">
        <thead><tr><th>User</th><th>State</th><th>Tier</th><th>Machine</th><th>Last running</th><th className="actions">Actions</th></tr></thead>
        <tbody>
          {workspaces.map((workspace) => {
            const state = stateOf(workspace);
            const blocker = restartBlocker(workspace);
            const failed = workspace.state === "error" ? workspace.error : null;
            return <tr key={workspace.id}>
              <td>{workspace.account.email == null ? <code className="admin-id">{workspace.account.id}</code> : <span title={workspace.account.id}>{workspace.account.email}</span>}</td>
              <td><span className={`admin-pill is-${state.tone}${state.busy ? " is-busy" : ""}`} title={failed ?? undefined}>{state.label}</span>{failed != null ? <small className="admin-error">{failed}</small> : null}</td>
              <td>{workspace.tier}</td>
              <td>{workspace.machine ?? <span className="muted">Not placed</span>}</td>
              <td title={workspace.lastReadyAt == null ? undefined : new Date(workspace.lastReadyAt).toLocaleString()}>{lastRunningText(workspace.lastReadyAt, now)}</td>
              <td className="actions"><span title={blocker ?? undefined}><button type="button" disabled={pending != null || blocker != null} aria-label={`Restart ${userText(workspace)}`} onClick={() => void restart(workspace)}>{pending === workspace.id ? "Restarting…" : "Restart"}</button></span></td>
            </tr>;
          })}
        </tbody>
      </table>
      {workspaces.length === 0 ? <p className="muted admin-empty">No workspaces yet.</p> : null}</div></div>
    </>}
  </section>;
}
